import { API_BASE } from "./constants";

export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {number} status
   * @param {unknown} body
   * @param {string | null} [code] stable error code from API (e.g. INVALID_CREDENTIALS)
   * @param {number | null} [retryAfterSeconds] e.g. VERIFY_RESEND_COOLDOWN (429)
   */
  constructor(message, status, body, code = null, retryAfterSeconds = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code = code;
    /** @type {number | null} */
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Prefer API `message`, optional per-code override, then fallback.
 * @param {unknown} err
 * @param {{ fallback?: string, byCode?: Record<string, string> }} [opts]
 * @returns {string}
 */
const emailTakenIdMessage =
  "Email ini sudah terdaftar. Gunakan masuk atau alamat email lain.";

/** @param {unknown} msg */
function looksLikeUsersEmailUniqueViolation(msg) {
  if (typeof msg !== "string") return false;
  return (
    msg.includes("users_email_key") &&
    (msg.includes("23505") ||
      msg.includes("duplicate key") ||
      msg.includes("unique constraint"))
  );
}

export function getUserFacingMessage(err, opts = {}) {
  const fallback = opts.fallback ?? "Terjadi kesalahan.";
  const byCode = opts.byCode ?? {};
  if (err instanceof ApiError) {
    if (err.code && Object.prototype.hasOwnProperty.call(byCode, err.code)) {
      return byCode[err.code];
    }
    if (looksLikeUsersEmailUniqueViolation(err.message)) {
      return emailTakenIdMessage;
    }
    if (err.message) return err.message;
  }
  return fallback;
}

export function getTokens() {
  if (typeof window === "undefined") return { access: "", refresh: "" };
  return {
    access: localStorage.getItem("halalin_access") ?? "",
    refresh: localStorage.getItem("halalin_refresh") ?? "",
  };
}

export function setTokens(access, refresh) {
  localStorage.setItem("halalin_access", access);
  localStorage.setItem("halalin_refresh", refresh);
}

export function clearTokens() {
  localStorage.removeItem("halalin_access");
  localStorage.removeItem("halalin_refresh");
}

export const SESSION_EXPIRED_EVENT = "halalin:session-expired";

function notifySessionExpired() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

async function refreshAccess() {
  const { refresh } = getTokens();
  if (!refresh) {
    clearTokens();
    notifySessionExpired();
    return null;
  }
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  if (!res.ok) {
    clearTokens();
    notifySessionExpired();
    return null;
  }
  const json = await res.json();
  const data = json.data;
  if (data?.access_token && data?.refresh_token) {
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  }
  clearTokens();
  notifySessionExpired();
  return null;
}

/**
 * @param {string} path
 * @param {RequestInit & { skipAuth?: boolean }} [init]
 */
export async function apiFetch(path, init = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const headers = new Headers(init.headers);
  const skipAuth = init.skipAuth === true;
  if (skipAuth) {
    headers.delete("Authorization");
  } else {
    const { access } = getTokens();
    if (access) headers.set("Authorization", `Bearer ${access}`);
  }
  // Many handlers use c.Bind().Body; without this header, JSON request bodies are ignored.
  if (
    init.body != null &&
    typeof init.body === "string" &&
    !headers.has("Content-Type")
  ) {
    const t = init.body.trimStart();
    if (t.startsWith("{") || t.startsWith("[")) {
      headers.set("Content-Type", "application/json; charset=utf-8");
    }
  }
  const { skipAuth: _skip, ...fetchInit } = init;
  let res = await fetch(url, {
    ...fetchInit,
    headers,
    credentials: skipAuth ? "omit" : fetchInit.credentials,
  });

  if (res.status === 401 && !skipAuth) {
    const newAccess = await refreshAccess();
    if (newAccess) {
      headers.set("Authorization", `Bearer ${newAccess}`);
      res = await fetch(url, {
        ...fetchInit,
        headers,
        credentials: fetchInit.credentials,
      });
    }
  }
  if (res.status === 401 && !skipAuth) {
    clearTokens();
    notifySessionExpired();
  }

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { raw: text };
  }

  if (!res.ok) {
    let errMsg;
    /** @type {string | null} */
    let errCode = null;
    /** @type {number | null} */
    let retryAfterSeconds = null;
    if (json && typeof json === "object" && json !== null && "error" in json) {
      const e = json.error;
      if (typeof e === "string") {
        errMsg = e;
      } else if (e && typeof e === "object") {
        errMsg = typeof e.message === "string" ? e.message : undefined;
        errCode = typeof e.code === "string" ? e.code : null;
        if (typeof e.retry_after_seconds === "number" && Number.isFinite(e.retry_after_seconds)) {
          retryAfterSeconds = Math.max(0, Math.floor(e.retry_after_seconds));
        }
      }
    }
    throw new ApiError(errMsg ?? res.statusText, res.status, json, errCode, retryAfterSeconds);
  }

  return json;
}

/**
 * Authenticated fetch returning the raw Response (for file download / HTML preview).
 * @param {string} path
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export async function apiFetchResponse(path, init = {}) {
  const { skipAuth: skipAuthFlag, ...fetchInit } = init;
  const skipAuth = skipAuthFlag === true;
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const headers = new Headers(fetchInit.headers);
  if (skipAuth) {
    headers.delete("Authorization");
  } else {
    const { access } = getTokens();
    if (access) headers.set("Authorization", `Bearer ${access}`);
  }
  let res = await fetch(url, {
    ...fetchInit,
    headers,
    credentials: skipAuth ? "omit" : fetchInit.credentials,
  });
  if (!skipAuth && res.status === 401) {
    const newAccess = await refreshAccess();
    if (newAccess) {
      headers.set("Authorization", `Bearer ${newAccess}`);
      res = await fetch(url, { ...fetchInit, headers });
    }
  }
  if (!skipAuth && res.status === 401) {
    clearTokens();
    notifySessionExpired();
  }
  if (!res.ok) {
    const text = await res.text();
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }
    let errMsg = text || res.statusText;
    /** @type {string | null} */
    let errCode = null;
    if (json && typeof json === "object" && json !== null) {
      if ("error" in json) {
        const e = json.error;
        if (typeof e === "string") {
          errMsg = e;
        } else if (e && typeof e === "object" && typeof e.message === "string") {
          errMsg = e.message;
        }
      }
      if (typeof json.code === "string") {
        errCode = json.code;
      } else if (
        json.error &&
        typeof json.error === "object" &&
        typeof json.error.code === "string"
      ) {
        errCode = json.error.code;
      }
    }
    throw new ApiError(errMsg, res.status, json || text, errCode, null);
  }
  return res;
}

/**
 * Compile and download Halal SJPH Manual package (ZIP: truncated DOCX + lampiran PDFs). POST opens gate server-side.
 * @param {string} clientid client UUID
 * @param {string} [filenameFallback]
 */
export async function downloadHalalManualZip(clientId, filenameFallback = "Manual_SJPH.zip") {
  const res = await apiFetchResponse(
    `/clients/${encodeURIComponent(clientId)}/manual/halal`,
    { method: "POST" },
  );
  const blob = await res.blob();
  let name = filenameFallback;
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (star) {
      try {
        name = decodeURIComponent(star[1].replace(/['"]/g, ""));
      } catch {
        name = star[1];
      }
    } else {
      const m = cd.match(/filename="([^"]+)"/i) || cd.match(/filename=([^;\s]+)/i);
      if (m) name = m[1].replace(/['"]/g, "").trim();
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || filenameFallback;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Download payment invoice PDF (pemohon self or superadmin).
 * @param {string} paymentId UUID
 * @param {string} [filenameFallback]
 */
export async function downloadInvoicePDF(paymentId, filenameFallback = "invoice.pdf") {
  const res = await apiFetchResponse(
    `/payments/${encodeURIComponent(paymentId)}/invoice.pdf`,
  );
  const blob = await res.blob();
  let name = filenameFallback;
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (star) {
      try {
        name = decodeURIComponent(star[1].replace(/['"]/g, ""));
      } catch {
        name = star[1];
      }
    } else {
      const m = cd.match(/filename="([^"]+)"/i) || cd.match(/filename=([^;\s]+)/i);
      if (m) name = m[1].replace(/['"]/g, "").trim();
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || filenameFallback;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Download payment kwitansi PDF (pemohon self or superadmin).
 * @param {string} paymentId UUID
 * @param {string} [filenameFallback]
 */
export async function downloadKwitansiPDF(paymentId, filenameFallback = "kwitansi.pdf") {
  const res = await apiFetchResponse(
    `/payments/${encodeURIComponent(paymentId)}/kwitansi.pdf`,
  );
  const blob = await res.blob();
  let name = filenameFallback;
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const star = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (star) {
      try {
        name = decodeURIComponent(star[1].replace(/['"]/g, ""));
      } catch {
        name = star[1];
      }
    } else {
      const m = cd.match(/filename="([^"]+)"/i) || cd.match(/filename=([^;\s]+)/i);
      if (m) name = m[1].replace(/['"]/g, "").trim();
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || filenameFallback;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * @template T
 * @param {{ success?: boolean; data?: T }} json
 * @returns {T}
 */
export function unwrap(json) {
  if (json?.data === undefined) {
    throw new Error("Invalid API response");
  }
  return json.data;
}

/** @returns {Promise<{ message?: string }>} */
export async function requestPasswordReset(email) {
  const json = await apiFetch("/auth/forgot-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: String(email).trim() }),
    skipAuth: true,
  });
  return unwrap(json);
}

/** @returns {Promise<{ message?: string }>} */
export async function resetPasswordWithToken(token, password) {
  const json = await apiFetch("/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: String(token).trim(), password }),
    skipAuth: true,
  });
  return unwrap(json);
}

/** @returns {Promise<Array<{ code: string; name: string; description?: string; price_idr: number; sort_order?: number }>>} */
export async function fetchCatalogPackages() {
  try {
    const json = await apiFetch("/packages", { skipAuth: true });
    return unwrap(json);
  } catch (e) {
    // Fiber v3 can run USE /api/v1 (RequireAuth) before GET /packages for unauthenticated requests.
    // Retry with Bearer when the user already has a session (e.g. superadmin payments, onboarding).
    if (e instanceof ApiError && e.status === 401 && getTokens().access) {
      const json = await apiFetch("/packages");
      return unwrap(json);
    }
    throw e;
  }
}

/**
 * @param {string[]} packageCodes
 * @returns {Promise<Record<string, unknown>>}
 */
export async function patchMyPaymentPackages(packageCodes) {
  const json = await apiFetch("/payments/my/packages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ packages: packageCodes }),
  });
  return unwrap(json);
}

/**
 * Start or update add-on checkout (pemohon with at least one verified package).
 * @param {string[]} packageCodes
 * @returns {Promise<Record<string, unknown>>}
 */
export async function postMyPaymentAddon(packageCodes) {
  const json = await apiFetch("/payments/my/addon", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ packages: packageCodes }),
  });
  return unwrap(json);
}

/**
 * @param {string} code
 * @returns {Promise<Record<string, unknown>>}
 */
export async function applyPaymentPromo(code) {
  const json = await apiFetch("/payments/apply-promo", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code: String(code).trim() }),
  });
  return unwrap(json);
}

/** @returns {Promise<Record<string, unknown>>} */
export async function removePaymentPromo() {
  const json = await apiFetch("/payments/promo", { method: "DELETE" });
  return unwrap(json);
}

/** @returns {Promise<Record<string, unknown>>} */
export async function fetchMyReferralCodeMeta() {
  const json = await apiFetch("/me/referral-code");
  return unwrap(json);
}

/** @returns {Promise<Record<string, unknown>>} */
export async function fetchMyReferralDisbursements() {
  const json = await apiFetch("/me/referral-disbursements");
  return unwrap(json);
}

/** @param {{ amount_idr: number, bank_instructions?: string, pemohon_notes?: string }} payload */
export async function submitReferralDisbursement(payload) {
  const json = await apiFetch("/me/referral-disbursements", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return unwrap(json);
}

/** @returns {Promise<Record<string, unknown>>} */
export async function requestMyReferralCode() {
  const json = await apiFetch("/me/referral-code/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  return unwrap(json);
}

/** @param {string} usageId @returns {Promise<Record<string, unknown>>} */
export async function adminRecalcReferralUsage(usageId) {
  const json = await apiFetch(`/admin/referrals/usages/${usageId}/recalculate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  return unwrap(json);
}
